import { useEffect, useState } from "react";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

const SLOTS = [
  { key: "preschool", label: "Pre-School" },
  { key: "grade",     label: "Grade School" },
  { key: "junior",    label: "Junior High" },
  { key: "senior",    label: "Senior High" },
];

const MB = 1024 * 1024;
const MAX_IMAGE = 10 * MB;

export default function ProgramImagesPage() {
  const user = getUser();
  const isAdmin = user?.role === "Admin" || user?.role === "SuperAdmin";

  const [images, setImages] = useState({});
  const [busySlot, setBusySlot] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => { load(); }, []);

  async function load() {
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/program-images`);
      if (!res.ok) throw new Error("Failed to load");
      setImages(await res.json());
    } catch (e) { setError(e.message); }
  }

  async function onPick(slot, e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;

    if (!f.type.startsWith("image/")) {
      setError("Only image files are allowed.");
      return;
    }
    if (f.size > MAX_IMAGE) {
      setError(`Image is ${(f.size / MB).toFixed(1)} MB — max 10 MB.`);
      return;
    }

    setError(""); setOk(""); setBusySlot(slot);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API_BASE}/api/program-images/${slot}/media`, {
        method: "POST",
        headers: {
          "X-User-Role": user?.role || "",
          "X-Username":  user?.username || "",
        },
        body: fd,
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Upload failed (${res.status})`));
      const data = await res.json();
      // bust cache so the new image shows immediately
      setImages(prev => ({ ...prev, [slot]: `${data.mediaUrl}?t=${Date.now()}` }));
      setOk(`${slot} image updated.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusySlot("");
    }
  }

  async function clearSlot(slot) {
    if (!window.confirm("Remove this image?")) return;
    setBusySlot(slot); setError(""); setOk("");
    try {
      const res = await fetch(`${API_BASE}/api/program-images/${slot}/media`, {
        method: "DELETE",
        headers: { "X-User-Role": user?.role || "" },
      });
      if (!res.ok) throw new Error(`Remove failed (${res.status})`);
      setImages(prev => ({ ...prev, [slot]: "" }));
      setOk(`${slot} image removed.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusySlot("");
    }
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: 40 }}>
        <h2>Program Images</h2>
        <p style={{ color: "#6b6b72" }}>Admin access required.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "32px 48px", maxWidth: 1100, margin: "0 auto" }}>
      <h2 style={{ color: "#4a1212", margin: "0 0 6px" }}>Program Images</h2>
      <p style={{ color: "#6b6b72", marginTop: 0 }}>
        Upload one image per level. Uploading a new image replaces the existing one.
      </p>

      {error && <div style={msgBad}>{error}</div>}
      {ok && <div style={msgGood}>{ok}</div>}

      <div style={grid}>
        {SLOTS.map(({ key, label }) => {
          const url = images[key];
          const busy = busySlot === key;
          return (
            <div key={key} style={card}>
              <div style={cardHead}>{label}</div>
              <div style={{ ...preview, backgroundImage: url ? `url(${url})` : "none" }}>
                {!url && <span style={placeholder}>No image</span>}
              </div>
              <div style={row}>
                <label style={{ ...btn, ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
                  {busy ? "Uploading…" : (url ? "Replace" : "Upload")}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => onPick(key, e)}
                    disabled={busy}
                  />
                </label>
                {url && (
                  <button
                    type="button"
                    style={{ ...btn, ...btnGhost }}
                    onClick={() => clearSlot(key)}
                    disabled={busy}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const msgBad = { background: "#ffe8e8", border: "1px solid #e9b0b0", color: "#8a1f1f", padding: "10px 14px", borderRadius: 8, marginBottom: 12 };
const msgGood = { background: "#e6f6ec", border: "1px solid #aedcc0", color: "#1e6a3a", padding: "10px 14px", borderRadius: 8, marginBottom: 12 };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 20 };
const card = { background: "#fff", border: "1px solid #e8d9b8", borderRadius: 12, padding: 16, boxShadow: "0 1px 3px rgba(74,18,18,.06)" };
const cardHead = { fontWeight: 800, color: "#4a1212", marginBottom: 10 };
const preview = {
  aspectRatio: "4 / 5",
  borderRadius: 8,
  background: "#f5ead6 center/cover no-repeat",
  display: "flex", alignItems: "center", justifyContent: "center",
  marginBottom: 12, border: "1px solid #ead7a8",
};
const placeholder = { color: "#b59a66", fontSize: 14, fontWeight: 600 };
const row = { display: "flex", gap: 8 };
const btn = { flex: 1, padding: "10px 14px", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", textAlign: "center", border: "1px solid transparent" };
const btnPrimary = { background: "#6b1d1d", color: "#fff", border: "1px solid #6b1d1d" };
const btnGhost = { background: "#fff", color: "#6b1d1d", border: "1px solid #d8c9a2" };
